import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Fish, Waves, Camera, Award, Shield } from "lucide-react";
import logo from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";

export default function Index() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen">
      <section id="main-content" tabIndex={-1} className="relative min-h-screen flex items-center justify-center overflow-hidden wave-gradient" role="main" aria-label="Welcome page">
        <div className="absolute inset-0 opacity-10">
          <Waves className="absolute top-20 left-20 w-48 h-48 animate-wave text-primary" />
          <Fish className="absolute bottom-32 right-32 w-32 h-32 animate-wave text-accent" style={{ animationDelay: "1s" }} />
          <Waves className="absolute top-60 right-60 w-24 h-24 animate-wave text-primary" style={{ animationDelay: "2s" }} />
        </div>

        <div className="container relative z-10 text-center space-y-8 px-4">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Upper Room Fishing Club" className="h-32 w-auto" />
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-gradient mb-6">
            Upper Room Fishing Club
          </h1>
          
          <p className="text-xl md:text-2xl text-foreground/90 max-w-2xl mx-auto mb-8">
            Record, verify, and showcase your trophy catches. 
            Join anglers worldwide in the ultimate fishing achievement platform.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" role="group" aria-label="Authentication actions">
            <Button 
              size="lg" 
              className="ocean-gradient text-lg px-8 py-6"
              onClick={() => navigate("/auth")}
              aria-label="Get started - Create a new account"
            >
              Get Started
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-6 border-2"
              onClick={() => navigate("/auth")}
              aria-label="Sign in to your account"
            >
              Sign In
            </Button>
          </div>

          <section aria-label="Key features" className="grid md:grid-cols-3 gap-8 mt-20 max-w-4xl mx-auto">
            <article className="p-6 rounded-xl bg-card/50 backdrop-blur border border-border">
              <Camera className="h-12 w-12 text-primary mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-bold mb-2">Photo Verification</h2>
              <p className="text-muted-foreground">
                Capture and verify every catch with photo evidence
              </p>
            </article>

            <article className="p-6 rounded-xl bg-card/50 backdrop-blur border border-border">
              <Shield className="h-12 w-12 text-primary mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-bold mb-2">Official Records</h2>
              <p className="text-muted-foreground">
                Weigh station verified catches with digital signatures
              </p>
            </article>

            <article className="p-6 rounded-xl bg-card/50 backdrop-blur border border-border">
              <Award className="h-12 w-12 text-primary mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-bold mb-2">Leaderboards</h2>
              <p className="text-muted-foreground">
                Compete with anglers and track your achievements
              </p>
            </article>
          </section>
        </div>
      </section>
    </div>
  );
}